import type { TaskStatus } from '@dyspatch/core';
import { MessageSquareWarning } from 'lucide-react';
import { type CSSProperties, type ReactNode, useId, useMemo } from 'react';
import { Empty, PrMark, RepoChip } from '@/components/common/bits';
import { ProgressBar } from '@/components/common/ProgressBar';
import { TaskIcon } from '@/components/common/StatusIcon';
import { Badge } from '@/components/ui/badge';
import { PausedChip } from '@/features/actions/PauseActions';
import { dependencyNote } from '@/lib/dependencies';
import { HELD_STATUSES, heldNote, progressStep } from '@/lib/fleet';
import { edgePath, layoutGraph, NODE_HEIGHT, NODE_WIDTH, type PlacedNode } from '@/lib/graph';
import { cn } from '@/lib/utils';

/**
 * The job as a graph (docs/JOB-LIFECYCLE.md 2.8).
 *
 * **One component, two phases, and no flag that says which.** Before Go a node
 * is a draft: the icon, the key, the title, the repo, and an "added" ring if an
 * amendment put it there. After Go the same node carries whatever live state it
 * has picked up: the status icon changes shape, the device running it takes the
 * meta line, the agent's own "3 of 7" rides the bottom edge, the draft or ready
 * PR is named, findings nobody has triaged wear a badge, and a paused step wears
 * the chip the rest of the console already uses. A node draws what it has, so
 * the two phases are one piece of code reading one set of rows rather than two
 * that have to be kept in agreement. A schedule's template is the third caller:
 * it passes draft nodes with no ids and gets the plan phase for nothing.
 *
 * **The picture is HTML over SVG.** Edges are orthogonal paths in one absolutely
 * positioned `<svg>`; the nodes over it are real `<button>` elements, or plain
 * boxes with a screen reader line where the caller gave no handler. That is the
 * whole reason for the arrangement: a node that opens something has to be
 * reachable by Tab, pressable by Enter and announced with its state, and an
 * `<svg><rect>` is none of those. The cards are opaque, so an edge spanning
 * three columns reads as passing behind the ones in its way.
 *
 * **Phones get the flat list.** Under `sm` the same nodes render as rows, out of
 * the same array, with the same handler. The graph is a wide view of the rows,
 * never a second source of truth.
 */
export interface GraphTask {
  /** Absent on a schedule's template, whose nodes are not task rows yet. */
  id?: string | null;
  key: string;
  title: string;
  repo?: string | null;
  /** Absent means a draft nobody has approved. */
  status?: TaskStatus | null;
  dependsOn: readonly string[];
  assignedWorkerName?: string | null;
  /** Why the scheduler is not offering a queued step, if it is not. */
  holdReason?: string | null;
  holdExplanation?: string | null;
  progress?: { done: number; total: number; current?: string | null } | null;
  branch?: string | null;
  prUrl?: string | null;
  prDraft?: boolean;
  pausedAt?: string | null;
}

export function JobGraph({
  tasks,
  added,
  findings,
  selectedKey,
  onSelect,
  empty,
  className,
}: {
  tasks: readonly GraphTask[];
  /** Keys an amendment added that nobody has approved yet; drawn with a ring. */
  added?: ReadonlySet<string>;
  /** Per task key, how many agent findings nobody has filed as an issue. */
  findings?: ReadonlyMap<string, number>;
  selectedKey?: string | null;
  onSelect?: (task: GraphTask) => void;
  /** What an empty graph says. */
  empty?: ReactNode;
  className?: string;
}) {
  // Unique per instance: two graphs on one page sharing a marker id would both
  // point at whichever of them mounted last.
  const rawId = useId();
  const arrowId = `dy-arrow-${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;
  const litArrowId = `${arrowId}-lit`;
  const layout = useMemo(() => layoutGraph(tasks), [tasks]);
  const byKey = useMemo(() => {
    const map = new Map<string, GraphTask>();
    for (const task of tasks) if (!map.has(task.key)) map.set(task.key, task);
    return map;
  }, [tasks]);
  const placed = useMemo(() => {
    const map = new Map<string, PlacedNode>();
    for (const node of layout.nodes) map.set(node.key, node);
    return map;
  }, [layout]);

  if (!layout.nodes.length) return <Empty>{empty ?? 'No tasks to draw yet.'}</Empty>;

  return (
    <div className={className}>
      {/*
        The scroller is the graph's own, and it is why the page body never scrolls
        sideways: the canvas inside is exactly as wide as the layout says, and
        this box clips it.
      */}
      <div className="dy-scroll hidden overflow-x-auto sm:block">
        <div className="w-max p-4">
          <div
            className="relative"
            style={{ width: `${layout.width}px`, height: `${layout.height}px` }}
          >
            <svg
              className="absolute top-0 left-0"
              width={layout.width}
              height={layout.height}
              aria-hidden
            >
              {/*
                One arrowhead per colour. A marker does not inherit the stroke of
                the path that references it, so a lit edge pointed at with a grey
                arrow is the one place the highlight would come apart.
              */}
              <defs>
                <Arrowhead id={arrowId} fill="var(--dy-line-2)" />
                <Arrowhead id={litArrowId} fill="var(--dy-gate)" />
              </defs>
              {layout.edges.map((edge) => {
                const from = placed.get(edge.from);
                const to = placed.get(edge.to);
                if (!from || !to) return null;
                const lit = selectedKey === edge.from || selectedKey === edge.to;
                return (
                  <path
                    key={`${edge.from}->${edge.to}`}
                    d={edgePath(from, to)}
                    fill="none"
                    stroke={lit ? 'var(--dy-gate)' : 'var(--dy-line-2)'}
                    strokeWidth={lit ? 2 : 1.25}
                    strokeLinejoin="round"
                    markerEnd={`url(#${lit ? litArrowId : arrowId})`}
                  />
                );
              })}
            </svg>
            {layout.nodes.map((node) => {
              const task = byKey.get(node.key);
              if (!task) return null;
              return (
                <NodeCard
                  key={node.key}
                  task={task}
                  byKey={byKey}
                  added={added?.has(node.key) ?? false}
                  findings={findings?.get(node.key) ?? 0}
                  selected={selectedKey === node.key}
                  onSelect={onSelect}
                  style={{
                    position: 'absolute',
                    left: `${node.x}px`,
                    top: `${node.y}px`,
                    width: `${NODE_WIDTH}px`,
                    height: `${NODE_HEIGHT}px`,
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>

      <ul className="divide-line divide-y sm:hidden">
        {layout.nodes.map((node) => {
          const task = byKey.get(node.key);
          if (!task) return null;
          return (
            <FlatRow
              key={node.key}
              task={task}
              byKey={byKey}
              added={added?.has(node.key) ?? false}
              findings={findings?.get(node.key) ?? 0}
              selected={selectedKey === node.key}
              onSelect={onSelect}
            />
          );
        })}
      </ul>
    </div>
  );
}

function Arrowhead({ id, fill }: { id: string; fill: string }) {
  return (
    <marker
      id={id}
      viewBox="0 0 8 8"
      refX="7"
      refY="4"
      markerWidth="5"
      markerHeight="5"
      orient="auto-start-reverse"
    >
      <path d="M 0 1 L 7 4 L 0 7 z" fill={fill} />
    </marker>
  );
}

/**
 * The agent's todo list, but only while it is running.
 *
 * `taskNote` draws the line on the task row under exactly this condition, and
 * the number comes from the same `progressStep`, so a node and the sheet over
 * it can never print two different steps for one task. After the run the
 * progress column is the last thing the agent said rather than where anything
 * is now, and a bar frozen at five sevenths on a merged step is a lie.
 */
function liveProgress(task: GraphTask) {
  return task.status === 'running' && task.progress ? task.progress : null;
}

/** What a node says about itself to a screen reader, in one sentence. */
function describe(
  task: GraphTask,
  added: boolean,
  findings: number,
  byKey: ReadonlyMap<string, GraphTask>,
): string {
  const parts = [`${task.key}: ${task.title}`, task.status ?? 'draft'];
  if (added) parts.push('added by an amendment, not yet approved');
  if (task.pausedAt) parts.push('paused');
  if (HELD_STATUSES.includes(task.status ?? 'draft') && task.assignedWorkerName) {
    parts.push(`on ${task.assignedWorkerName}`);
  }
  // An unmet dependency is the reason a queued step is not moving, ahead of
  // the scheduler's own hold: `dispatchableTasks` never hands a step with one
  // to the scheduler, so it never carries a hold reason of its own to report.
  const dep = dependencyNote(task, byKey);
  if (dep) {
    parts.push(dep.text);
  } else {
    const hold = task.status === 'queued' ? heldNote(task) : null;
    if (hold) parts.push(`held: ${hold}`);
  }
  const progress = liveProgress(task);
  if (progress) parts.push(`step ${progressStep(progress)} of ${progress.total}`);
  if (findings) parts.push(`${findings} finding${findings === 1 ? '' : 's'} not filed`);
  return parts.join(', ');
}

/**
 * The line under the title: where the step got to, and the device holding it.
 *
 * The name is only added while a device actually holds the row (`HELD_STATUSES`,
 * core's list mirrored and pinned). `assigned_worker_name` is not cleared when a
 * run ends, so a merged step whose line read only "sam-mbp" would look like a
 * step still running on sam-mbp, which is exactly the state the icon beside it
 * says it is not in.
 *
 * A queued step the scheduler is refusing to offer says so instead. That is the
 * one case where the status word alone is a lie by omission: a step nothing can
 * take because every device is offline reads identically to one simply waiting
 * its turn, and the graph is now the widest place a person looks at a job.
 *
 * A queued step naming an unmet dependency outranks that: it says so instead,
 * naming what it is waiting on, or which of those is itself stuck
 * (docs/JOB-LIFECYCLE.md 2.10) - the one thing the plain status word "queued"
 * cannot tell a reader on its own.
 */
function metaLine(task: GraphTask, byKey: ReadonlyMap<string, GraphTask>): string {
  const status = task.status ?? 'draft';
  if (HELD_STATUSES.includes(status) && task.assignedWorkerName) {
    return `${status} · ${task.assignedWorkerName}`;
  }
  const dep = dependencyNote(task, byKey);
  if (dep) return dep.text;
  const hold = status === 'queued' ? heldNote(task) : null;
  return hold ? `held · ${hold}` : status;
}

/** "4 of 7", the same step the row and the sheet print. */
function ProgressCount({ task, className }: { task: GraphTask; className?: string }) {
  const progress = liveProgress(task);
  if (!progress) return null;
  return (
    <span
      className={cn('text-fg-3 tnum shrink-0 text-2xs', className)}
      title={progress.current ?? undefined}
    >
      {progressStep(progress)} of {progress.total}
    </span>
  );
}

function Marks({ task, added, findings }: { task: GraphTask; added: boolean; findings: number }) {
  return (
    <>
      {added ? (
        <Badge variant="gate" title="Added by an amendment. It waits for approval.">
          added
        </Badge>
      ) : null}
      {findings ? (
        <Badge
          variant="warning"
          title={`${findings} agent finding${findings === 1 ? '' : 's'} nobody has filed as an issue.`}
        >
          <MessageSquareWarning className="size-2.5" aria-hidden />
          {findings}
        </Badge>
      ) : null}
      {task.pausedAt ? <PausedChip /> : null}
    </>
  );
}

function NodeCard({
  task,
  byKey,
  added,
  findings,
  selected,
  onSelect,
  style,
}: {
  task: GraphTask;
  byKey: ReadonlyMap<string, GraphTask>;
  added: boolean;
  findings: number;
  selected: boolean;
  onSelect?: (task: GraphTask) => void;
  style: CSSProperties;
}) {
  const live = liveProgress(task);
  const label = describe(task, added, findings, byKey);
  const className = cn(
    'bg-card relative flex flex-col gap-1 overflow-hidden rounded-lg border p-2 text-left',
    'transition-colors duration-100',
    onSelect ? 'hover:border-line-2 hover:bg-raised/50' : 'cursor-default',
    added && 'border-gate-line border-dashed',
    selected && 'border-gate bg-sel',
  );
  const body = (
    <>
      <span className="flex min-w-0 items-center gap-1.5">
        <TaskIcon status={task.status ?? 'draft'} />
        <span className="text-fg-3 shrink-0 font-mono text-2xs tabular-nums">{task.key}</span>
        <span className="ml-auto flex shrink-0 items-center gap-1">
          <Marks task={task} added={added} findings={findings} />
        </span>
      </span>
      {/* 13px over two lines in a 240px card, which is where the node stopped
          being a shape and started being a sentence. See `NODE_WIDTH`. */}
      <span className="line-clamp-2 min-w-0 text-[0.8125rem] leading-[1.35]" title={task.title}>
        {task.title}
      </span>
      <span className="mt-auto flex min-w-0 items-center gap-1.5 overflow-hidden">
        {task.repo ? <RepoChip repo={task.repo} /> : null}
        <ProgressCount task={task} className="ml-auto" />
        <PrMark prUrl={task.prUrl} prDraft={task.prDraft} branch={task.branch} />
      </span>
      <span className="text-fg-3 min-w-0 truncate text-2xs">{metaLine(task, byKey)}</span>
      {/* The bar rides the card's bottom edge so it costs the layout no height,
          which is what lets every node be exactly `NODE_HEIGHT` tall. */}
      {live ? (
        <ProgressBar
          fraction={live.done / live.total}
          className="absolute inset-x-0 bottom-0 rounded-none"
        />
      ) : null}
    </>
  );

  // A node with nowhere to go is not a button. A caller that draws the graph
  // read-only (a schedule's template, before it is a job) would otherwise hand
  // a keyboard twenty tab stops that do nothing.
  if (!onSelect) {
    return (
      <div style={style} className={className}>
        <span className="sr-only">{label}</span>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      style={style}
      onClick={() => onSelect(task)}
      aria-current={selected ? true : undefined}
      aria-label={label}
      className={className}
    >
      {body}
    </button>
  );
}

/** The same node as a row, for anything narrower than `sm`. */
function FlatRow({
  task,
  byKey,
  added,
  findings,
  selected,
  onSelect,
}: {
  task: GraphTask;
  byKey: ReadonlyMap<string, GraphTask>;
  added: boolean;
  findings: number;
  selected: boolean;
  onSelect?: (task: GraphTask) => void;
}) {
  const label = describe(task, added, findings, byKey);
  const className = cn(
    'flex w-full items-start gap-2 px-gutter py-row text-left',
    onSelect ? 'hover:bg-raised/60' : 'cursor-default',
    selected && 'bg-sel',
  );
  const body = (
    <>
      <TaskIcon status={task.status ?? 'draft'} className="mt-[2px]" />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="text-fg-3 shrink-0 font-mono text-2xs tabular-nums">{task.key}</span>
          <span className="min-w-0 truncate text-sm">{task.title}</span>
        </span>
        <span className="text-fg-3 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs">
          {task.repo ? <RepoChip repo={task.repo} /> : null}
          <span>{metaLine(task, byKey)}</span>
          <ProgressCount task={task} />
          <PrMark prUrl={task.prUrl} prDraft={task.prDraft} branch={task.branch} />
          {task.dependsOn.length ? (
            <span className="bg-raised text-fg-2 shrink-0 rounded-sm px-1 font-mono">
              after {task.dependsOn.join(', ')}
            </span>
          ) : null}
          <Marks task={task} added={added} findings={findings} />
        </span>
      </span>
    </>
  );

  return (
    <li>
      {onSelect ? (
        <button
          type="button"
          onClick={() => onSelect(task)}
          aria-current={selected ? true : undefined}
          aria-label={label}
          className={className}
        >
          {body}
        </button>
      ) : (
        <div className={className}>
          <span className="sr-only">{label}</span>
          {body}
        </div>
      )}
    </li>
  );
}
